import React, { useMemo } from 'react';
import { useError } from 'contexts/error';
import { useQuery } from '@apollo/client/react';
import { Typography, BarChart } from 'components/ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { WhiteCard } from 'components/styles_components/card';
import InspectionDailyOverviewQuery from 'queries/inspection/daily_overview';
import Loader from 'components/loader';
import theme from 'theme';

const DailyOverview = () => {
  const setError = useError();
  const intl = useIntl();

  const {
    data = { inspectionDailyOverview: { overview: [] } },
    loading,
    error,
  } = useQuery(InspectionDailyOverviewQuery, {
    onError: () => {
      setError('error.inspections.daily_overview');
    },
  });

  const chartData = useMemo(() => {
    if (!data?.inspectionDailyOverview?.overview) {
      return [];
    }
    const overview = data.inspectionDailyOverview.overview;
    const dayNames = [
      intl.formatMessage({ id: 'day.monday.short' }),
      intl.formatMessage({ id: 'day.tuesday.short' }),
      intl.formatMessage({ id: 'day.wednesday.short' }),
      intl.formatMessage({ id: 'day.thursday.short' }),
      intl.formatMessage({ id: 'day.friday.short' }),
      intl.formatMessage({ id: 'day.saturday.short' }),
      intl.formatMessage({ id: 'day.sunday.short' }),
    ];
    const dataLength = Math.min(dayNames.length, overview.length);

    return overview.slice(0, dataLength).map((item, index) => ({
      day: dayNames[index],
      pending: item.pendingInspections || 0,
      completed: item.completedInspections || 0,
      canceled: item.canceledInspections || 0,
    }));
  }, [data, intl]);

  if (loading || error) {
    return <Loader />;
  }

  return (
    <WhiteCard>
      <Typography variant="h6" gutterBottom>
        <FormattedMessage id="chart.daily_overview" />
      </Typography>
      {chartData.length === 0 || chartData.every((item) => item.pending === 0 && item.completed === 0 && item.canceled === 0) ? (
        <Typography variant="body1" align="center" sx={{ py: 4 }}>
          <FormattedMessage id="chart.no_data" />
        </Typography>
      ) : (
      <BarChart
        height={400}
        dataset={chartData}
        series={[
          {
            dataKey: 'pending',
            label: intl.formatMessage({ id: 'chart.legend.pending_inspections' }),
            color: theme.palette.chart.pendingInspections,
            stack: 'inspections',
          },
          {
            dataKey: 'completed',
            label: intl.formatMessage({ id: 'chart.legend.completed_inspections' }),
            color: theme.palette.chart.completedInspections,
            stack: 'inspections',
          },
          {
            dataKey: 'canceled',
            label: intl.formatMessage({ id: 'chart.legend.canceled_inspections' }),
            color: theme.palette.chart.canceledInspections,
            stack: 'inspections',
          },
        ]}
        xAxis={[{ dataKey: 'day' }]}
        yAxis={[
          {
            label: intl.formatMessage({ id: 'chart.axis.number_of_inspections' }),
            tickMinStep: 1,
          },
        ]}
        legend={{ position: { vertical: 'top', horizontal: 'right' } }}
      />
      )}
    </WhiteCard>
  );
};

export default DailyOverview;
